/**
 * Lazy Variant Images Loader
 * Optimizes product page load by lazy loading variant images
 * Works with Impulse theme's variant image set system (alt text tags)
 */

class LazyVariantImages {
  constructor() {
    this.enabled = true;

    // Parse product JSON
    this.productData = this.getProductJSON();
    if (!this.productData) {
      console.warn('LazyVariantImages: Product JSON not found');
      this.enabled = false;
      return;
    }

    // Cache DOM elements
    this.container = document.querySelector('[data-section-type="product"]');
    if (!this.container) {
      this.enabled = false;
      return;
    }

    this.slidesContainer = this.container.querySelector('[data-product-photos]');
    if (!this.slidesContainer) {
      this.enabled = false;
      return;
    }

    // SH Updated: February 2026 - Dynamic tag prefix detection for non-color variants (e.g., EDITION)
    // Detect tag prefix from images (e.g., 'color', 'edition')
    this.tagPrefix = this.detectTagPrefix();

    // Detect Color option position (option1, option2, or option3)
    this.colorOptionIndex = this.findColorOptionIndex();

    // Detect settings
    this.settings = {
      thumbnailPosition: this.getThumbnailPosition(),
      zoomEnabled: this.isZoomEnabled(),
      mobileLayout: this.getMobileLayout(),
      hasVideos: this.detectVideos(),
      hasColorSwatches: this.detectColorSwatches()
    };

    // Group images by variant
    this.imagesByVariant = this.groupImagesByAltText();

    // Track loaded slides
    this.loadedSlides = new Set();

    // Mark videos as loaded (don't lazy load them)
    this.markVideosAsLoaded();

    // Initialize
    this.init();
  }

  /**
   * Check if product has any color-based image grouping
   * Returns false if all images are untagged (no #prefix_ tags in alt text)
   */
  hasColorGrouping() {
    const groups = Object.keys(this.imagesByVariant);
    // Only has 'untagged' key = no color grouping
    return groups.length > 1 || (groups.length === 1 && groups[0] !== 'untagged');
  }

  /**
   * Detect the tag prefix used in image alt text
   * Returns the prefix (e.g., 'color', 'edition', 'style') or 'color' as default
   * SH Updated: February 2026 - Added for non-color variant support
   */
  detectTagPrefix() {
    if (!this.productData || !this.productData.media) return 'color';

    for (const media of this.productData.media) {
      if (media.media_type !== 'image') continue;
      const alt = media.alt || '';
      // Match any #prefix_value pattern
      const match = alt.match(/#([a-z]+)_([a-z0-9-]+)/i);
      if (match) {
        const prefix = match[1].toLowerCase();
        console.log('LazyVariantImages: Detected tag prefix:', prefix);
        return prefix;
      }
    }
    return 'color'; // default
  }

  /**
   * Find which option index (0, 1, or 2) matches the tag prefix used in images
   * Falls back to checking for 'color' option, then defaults to index 0
   * SH Updated: February 2026 - Dynamic option detection based on tag prefix
   */
  findColorOptionIndex() {
    if (!this.productData || !this.productData.options) return 0;

    // First, try to find option matching detected tag prefix
    for (let i = 0; i < this.productData.options.length; i++) {
      const optionName = this.productData.options[i].toLowerCase();
      if (optionName === this.tagPrefix || optionName.includes(this.tagPrefix)) {
        console.log('LazyVariantImages: Found matching option at index', i, '(' + this.productData.options[i] + ') for prefix:', this.tagPrefix);
        return i;
      }
    }

    // Fallback: check for color/colour
    for (let i = 0; i < this.productData.options.length; i++) {
      const optionName = this.productData.options[i].toLowerCase();
      if (optionName === 'color' || optionName === 'colour' || optionName.includes('color')) {
        console.log('LazyVariantImages: Color option found at index', i);
        return i;
      }
    }

    return 0; // Default to option1
  }

  /**
   * Initialize lazy loading
   */
  init() {
    console.log('LazyVariantImages: Initializing...', {
      thumbnailPosition: this.settings.thumbnailPosition,
      variantGroups: Object.keys(this.imagesByVariant),
      totalImages: this.productData.media.length
    });

    // Wait for Flickity initialization
    this.waitForFlickity().then(() => {
      // Re-detect thumbnail position now that Flickity is ready
      // On mobile, Flickity is used even for "grid" mode
      this.settings.thumbnailPosition = this.getThumbnailPosition();
      console.log('LazyVariantImages: After Flickity init, mode:', this.settings.thumbnailPosition);

      this.bindEvents();
      this.initialLoad();
      this.setupIntersectionObserver();
      this.scheduleBackgroundPreload();
    });
  }

  /**
   * Setup Intersection Observer for scroll-based lazy loading
   * This loads images as they scroll into view (visible slides only)
   */
  setupIntersectionObserver() {
    // Skip if IntersectionObserver not supported
    if (!('IntersectionObserver' in window)) {
      console.log('LazyVariantImages: IntersectionObserver not supported, loading all');
      this.loadAllVisibleSlides();
      return;
    }

    const options = {
      root: null, // viewport
      rootMargin: '200px 0px', // Start loading 200px before visible
      threshold: 0.01 // Trigger when even 1% visible
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const slide = entry.target;
          const index = parseInt(slide.dataset.index);

          // Only load if slide is visible (not hidden by variant filtering)
          const style = window.getComputedStyle(slide);
          if (style.display !== 'none' && !isNaN(index)) {
            this.loadSlide(index);
            // Stop observing once loaded
            this.observer.unobserve(slide);
          }
        }
      });
    }, options);

    // Observe all placeholder slides
    const placeholders = this.slidesContainer.querySelectorAll('.product-main-slide.lazy-pending');
    placeholders.forEach(slide => {
      this.observer.observe(slide);
    });

    console.log('LazyVariantImages: Observing', placeholders.length, 'placeholders for scroll');
  }

  /**
   * Fallback: Load all visible slides if IntersectionObserver not supported
   */
  loadAllVisibleSlides() {
    const slides = this.slidesContainer.querySelectorAll('.product-main-slide.lazy-pending');
    slides.forEach(slide => {
      const style = window.getComputedStyle(slide);
      if (style.display !== 'none') {
        const index = parseInt(slide.dataset.index);
        if (!isNaN(index)) {
          this.loadSlide(index);
        }
      }
    });
  }

  /**
   * Wait for Flickity to initialize
   */
  waitForFlickity() {
    return new Promise((resolve) => {
      const check = () => {
        // slidesContainer itself can be the Flickity element
        const hasFlickity = this.slidesContainer.classList.contains('flickity-enabled') ||
                            this.slidesContainer.querySelector('.flickity-enabled');

        if (hasFlickity || this.settings.thumbnailPosition === 'grid') {
          resolve();
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    });
  }

  /**
   * Get product JSON from page
   */
  getProductJSON() {
    const jsonEl = document.getElementById('main-product-data');
    if (!jsonEl) return null;

    try {
      return JSON.parse(jsonEl.textContent);
    } catch (e) {
      console.error('LazyVariantImages: Failed to parse product JSON', e);
      return null;
    }
  }

  /**
   * Detect thumbnail position setting
   * On mobile, Flickity is always used regardless of desktop grid setting
   */
  getThumbnailPosition() {
    // Check if we're on mobile (Flickity will be used)
    const isMobile = window.innerWidth < 768;

    // On mobile, if Flickity is enabled, treat as slideshow not grid
    // Note: slidesContainer itself can be the Flickity element (has .flickity-enabled class)
    if (isMobile) {
      const hasFlickity = this.slidesContainer.classList.contains('flickity-enabled') ||
                          this.slidesContainer.querySelector('.flickity-enabled');
      if (hasFlickity) {
        console.log('LazyVariantImages: Mobile detected with Flickity - using slideshow mode');
        return 'beside'; // Use slideshow behavior on mobile
      }
    }

    const thumbContainer = this.container.querySelector('[data-product-thumbs]');

    if (!thumbContainer || thumbContainer.classList.contains('hide')) {
      return 'grid';
    }

    return thumbContainer.dataset.position || 'beside';
  }

  /**
   * Check if PhotoSwipe zoom is enabled
   */
  isZoomEnabled() {
    return this.container.querySelector('[data-photoswipe-src]') !== null;
  }

  /**
   * Get mobile layout setting
   */
  getMobileLayout() {
    const productSection = this.container.closest('[data-section-id]');
    return productSection?.dataset.mobileLayout || 'partial';
  }

  /**
   * Detect if product has videos
   */
  detectVideos() {
    return this.productData.media.some(m => m.media_type === 'video' || m.media_type === 'external_video');
  }

  /**
   * Detect if color swatches are enabled
   */
  detectColorSwatches() {
    return this.container.querySelector('.variant-input-wrap[data-handle="color"]') !== null;
  }

  /**
   * Mark video slides as already loaded
   */
  markVideosAsLoaded() {
    const slides = this.slidesContainer.querySelectorAll('.product-main-slide');
    slides.forEach((slide) => {
      const mediaType = slide.dataset.mediaType;
      if (mediaType === 'video' || mediaType === 'external_video' || mediaType === 'model') {
        const index = parseInt(slide.dataset.index);
        this.loadedSlides.add(index);
      }
    });
  }

  /**
   * Group images by alt text color tags
   */
  groupImagesByAltText() {
    const groups = {
      untagged: []
    };

    // SH Updated: February 2026 - Dynamic tag prefix matching (not just #color_)
    this.productData.media.forEach((media, index) => {
      // Skip non-images
      if (media.media_type !== 'image') {
        return;
      }

      const alt = media.alt || '';
      // Match any #prefix_value pattern (e.g., #color_black, #edition_lil-trashy)
      const match = alt.match(/#([a-z]+)_([a-z0-9-]+)/i);

      if (match) {
        const tagPrefix = match[1].toLowerCase();
        const colorValue = this.normalizeValue(match[2]);
        // Only group if tag prefix matches what we're looking for
        if (tagPrefix === this.tagPrefix) {
          if (!groups[colorValue]) {
            groups[colorValue] = [];
          }
          groups[colorValue].push({
            ...media,
            slideIndex: index
          });
        } else {
          groups.untagged.push({ ...media, slideIndex: index });
        }
      } else {
        // Untagged images show with all variants
        groups.untagged.push({
          ...media,
          slideIndex: index
        });
      }
    });

    return groups;
  }

  /**
   * Normalize variant value for matching
   */
  normalizeValue(string) {
    return string.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-$/, '')
      .replace(/^-/, '');
  }

  /**
   * Initial load of visible images
   */
  initialLoad() {
    // First, show the correct slides for the initially selected variant (grid mode only)
    this.showInitialVariantSlides();

    if (this.settings.thumbnailPosition === 'grid') {
      // If no color grouping, load first 4 untagged images for grid mode
      if (!this.hasColorGrouping()) {
        console.log('LazyVariantImages: Grid mode without color grouping - loading untagged images');
        const untaggedImages = this.imagesByVariant.untagged || [];
        untaggedImages.slice(0, 4).forEach(img => {
          this.loadSlide(img.slideIndex);
        });
        return;
      }

      // Grid mode: Load images based on variant color
      const currentVariant = this.getCurrentVariant();
      const colorValue = currentVariant ? this.getColorFromVariant(currentVariant) : null;
      const normalizedColor = colorValue ? this.normalizeValue(colorValue) : '';
      const imagesToLoad = this.imagesByVariant[normalizedColor] || [];

      console.log('LazyVariantImages: Grid mode initial load for variant', colorValue, '-', imagesToLoad.length, 'images');

      if (imagesToLoad.length > 0) {
        imagesToLoad.slice(0, 4).forEach(img => {
          this.loadSlide(img.slideIndex);
        });
      }

      // Also load untagged images
      this.imagesByVariant.untagged.slice(0, 4).forEach(img => {
        this.loadSlide(img.slideIndex);
      });
    } else {
      // Non-grid mode: Load images for slides currently in Flickity
      console.log('LazyVariantImages: Non-grid mode initial load - using Flickity cells');
      this.loadImagesForCurrentFlickity();
    }
  }

  /**
   * Show the correct slides for the initially selected variant
   * Grid mode: manually control display visibility
   * Non-grid mode: Flickity uses cellSelector to filter - don't interfere
   */
  showInitialVariantSlides() {
    // In non-grid mode (slideshow), Flickity handles filtering via cellSelector
    // Don't manually hide/show slides as it interferes with Flickity
    if (this.settings.thumbnailPosition !== 'grid') {
      console.log('LazyVariantImages: Non-grid mode - Flickity handles slide filtering via cellSelector');
      return;
    }

    // If no color grouping (product has no color variants/tagged images), show all slides
    if (!this.hasColorGrouping()) {
      console.log('LazyVariantImages: No color grouping - showing all slides');
      const slides = this.slidesContainer.querySelectorAll('.product-main-slide');
      slides.forEach(slide => {
        slide.style.display = '';
        slide.removeAttribute('aria-hidden');
      });
      return;
    }

    // Grid mode - manually control slide visibility
    const currentVariant = this.getCurrentVariant();
    if (!currentVariant) return;

    const colorValue = this.getColorFromVariant(currentVariant);
    if (!colorValue) return;

    // SH Updated: February 2026 - Use dynamic tag prefix instead of hardcoded 'color_'
    const normalizedColor = this.tagPrefix + '_' + this.normalizeValue(colorValue);

    console.log('LazyVariantImages: Grid mode - showing slides for initial variant', colorValue, '(' + normalizedColor + ')');

    const slides = this.slidesContainer.querySelectorAll('.product-main-slide');
    slides.forEach((slide, index) => {
      const slideGroup = slide.dataset.group;

      if (slideGroup === normalizedColor) {
        // Matches current variant - show it
        slide.style.display = '';
        slide.removeAttribute('aria-hidden');
      } else {
        // Hide everything else (including untagged images)
        slide.style.display = 'none';
        slide.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /**
   * Refresh Flickity after changing slide visibility
   */
  refreshFlickity() {
    // slidesContainer itself can be the Flickity element
    const flickityEl = this.slidesContainer.classList.contains('flickity-enabled')
      ? this.slidesContainer
      : this.slidesContainer.querySelector('.flickity-enabled');

    if (flickityEl && window.Flickity) {
      const flkty = window.Flickity.data(flickityEl);
      if (flkty) {
        // Reposition cells and select first visible slide
        flkty.reloadCells();
        flkty.resize();
        flkty.select(0, false, true);
        console.log('LazyVariantImages: Flickity refreshed');
      }
    }
  }

  /**
   * Get the currently selected variant
   */
  getCurrentVariant() {
    // Try to find selected variant from URL or form
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');

    if (variantId && this.productData.variants) {
      const variant = this.productData.variants.find(v => v.id.toString() === variantId);
      if (variant) return variant;
    }

    // Fallback to first available variant
    if (this.productData.variants && this.productData.variants.length > 0) {
      return this.productData.variants.find(v => v.available) || this.productData.variants[0];
    }

    return null;
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Variant change events
    this.container.addEventListener('variantChange', (evt) => {
      if (evt.detail && evt.detail.variant) {
        this.onVariantChange(evt.detail.variant);
      }
    });

    // Thumbnail clicks (not in grid mode)
    if (this.settings.thumbnailPosition !== 'grid') {
      this.bindThumbnailClicks();
    }

    // Flickity slide change
    if (this.settings.thumbnailPosition !== 'grid') {
      this.slidesContainer.addEventListener('settle.flickity', (evt) => {
        if (evt.detail !== undefined) {
          this.onSlideChange(evt.detail);
        }
      });
    }
  }

  /**
   * Bind thumbnail click events
   */
  bindThumbnailClicks() {
    const thumbs = this.container.querySelectorAll('[data-product-thumb]');
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', (evt) => {
        const index = parseInt(thumb.dataset.index);
        if (!isNaN(index)) {
          // Load target slide immediately
          this.loadSlide(index);
          // Preload adjacent slides
          this.loadSlide(index - 1);
          this.loadSlide(index + 1);
        }
      });
    });
  }

  /**
   * Handle variant change
   */
  onVariantChange(variant) {
    if (!variant) return;

    // Get color value from the correct option based on colorOptionIndex
    const colorValue = this.getColorFromVariant(variant);
    if (!colorValue) return;

    const variantValue = this.normalizeValue(colorValue);
    const imagesToLoad = this.imagesByVariant[variantValue] || [];

    console.log('LazyVariantImages: Variant changed to', colorValue, '(', variantValue, ') -', imagesToLoad.length, 'images');

    // In grid mode, toggle slide visibility manually
    // SH Updated: February 2026 - Use dynamic tag prefix instead of hardcoded 'color_'
    if (this.settings.thumbnailPosition === 'grid') {
      this.showSlidesForColor(this.tagPrefix + '_' + variantValue);

      // Load first few images immediately for instant display
      imagesToLoad.slice(0, 4).forEach(img => {
        this.loadSlide(img.slideIndex);
      });

      // Re-observe remaining placeholders so they load on scroll
      this.reobservePlaceholders();
    } else {
      // Non-grid mode: Theme's Flickity will reinitialize with new cellSelector
      // Wait for Flickity to reinitialize, then load images for new slides
      setTimeout(() => {
        this.loadImagesForCurrentFlickity();
      }, 200);
    }
  }

  /**
   * Load images for slides currently in Flickity's cell list
   * Used after Flickity reinitializes with a new cellSelector
   */
  loadImagesForCurrentFlickity() {
    // slidesContainer itself can be the Flickity element
    const flickityEl = this.slidesContainer.classList.contains('flickity-enabled')
      ? this.slidesContainer
      : this.slidesContainer.querySelector('.flickity-enabled');

    if (!flickityEl || !window.Flickity) {
      console.log('LazyVariantImages: loadImagesForCurrentFlickity - no Flickity found');
      return;
    }

    const flkty = window.Flickity.data(flickityEl);
    if (!flkty || !flkty.cells) {
      console.log('LazyVariantImages: loadImagesForCurrentFlickity - no Flickity data');
      return;
    }

    console.log('LazyVariantImages: Loading images for', flkty.cells.length, 'Flickity cells');

    // Load images for all cells in current Flickity instance
    flkty.cells.forEach((cell, i) => {
      const slide = cell.element;
      if (slide && slide.classList.contains('lazy-pending')) {
        const index = parseInt(slide.dataset.index);
        if (!isNaN(index)) {
          // Load first 2 immediately, rest with small delay
          if (i < 2) {
            this.loadSlide(index);
          } else {
            setTimeout(() => this.loadSlide(index), i * 50);
          }
        }
      }
    });
  }

  /**
   * Re-observe placeholders after variant change
   * This ensures newly visible slides get observed for lazy loading
   */
  reobservePlaceholders() {
    if (!this.observer) return;

    // Find all visible placeholders and observe them
    const placeholders = this.slidesContainer.querySelectorAll('.product-main-slide.lazy-pending');
    let observed = 0;

    placeholders.forEach(slide => {
      const style = window.getComputedStyle(slide);
      if (style.display !== 'none') {
        this.observer.observe(slide);
        observed++;
      }
    });

    if (observed > 0) {
      console.log('LazyVariantImages: Re-observing', observed, 'placeholders after variant change');
    }
  }

  /**
   * Show slides matching the specified color group
   * Following Impulse theme behavior: only show variant-specific images
   */
  showSlidesForColor(normalizedColor) {
    // If no color grouping (product has no color variants/tagged images), show all slides
    if (!this.hasColorGrouping()) {
      const slides = this.slidesContainer.querySelectorAll('.product-main-slide');
      slides.forEach(slide => {
        slide.style.display = '';
        slide.removeAttribute('aria-hidden');
      });
      return;
    }

    const slides = this.slidesContainer.querySelectorAll('.product-main-slide');
    slides.forEach((slide) => {
      const slideGroup = slide.dataset.group;

      if (slideGroup === normalizedColor) {
        // Matches current variant - show it
        slide.style.display = '';
        slide.removeAttribute('aria-hidden');
      } else {
        // Hide everything else (including untagged images)
        slide.style.display = 'none';
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    // For Flickity (mobile slider), refresh after changing visibility
    if (this.settings.thumbnailPosition !== 'grid') {
      this.refreshFlickity();
    }
  }

  /**
   * Get color value from variant using the correct option index
   */
  getColorFromVariant(variant) {
    const optionKeys = ['option1', 'option2', 'option3'];
    const optionKey = optionKeys[this.colorOptionIndex];
    return variant[optionKey] || variant.option1;
  }

  /**
   * Handle Flickity slide change
   */
  onSlideChange(index) {
    // Load current + adjacent slides
    this.loadSlide(index);
    this.loadSlide(index - 1);
    this.loadSlide(index + 1);
  }

  /**
   * Load a specific slide by index (data-index attribute)
   */
  loadSlide(index) {
    if (index < 0) return;
    if (this.loadedSlides.has(index)) return;

    // Find slide by data-index attribute (more reliable than array index)
    const slide = this.slidesContainer.querySelector(`.product-main-slide[data-index="${index}"]`);

    if (!slide) return;
    if (slide.dataset.loaded === 'true') return;
    if (!slide.classList.contains('lazy-pending')) return;

    // Build image element
    const imageWrapper = this.buildImageElement(slide);
    const placeholder = slide.querySelector('.lazy-placeholder');

    if (placeholder && imageWrapper) {
      placeholder.parentNode.replaceChild(imageWrapper, placeholder);
      slide.dataset.loaded = 'true';
      slide.classList.remove('lazy-pending');
      this.loadedSlides.add(index);

      // Bind PhotoSwipe click handler to new zoom button
      this.bindPhotoSwipeToSlide(slide);

      console.log('LazyVariantImages: Loaded slide', index);
    } else {
      // Try alternate structure - look for product-image-main container
      const imageMain = slide.querySelector('[data-product-image-main], .product-image-main');
      if (imageMain && imageWrapper) {
        // Clear existing content and add new image
        imageMain.innerHTML = '';
        imageMain.appendChild(imageWrapper);
        slide.dataset.loaded = 'true';
        slide.classList.remove('lazy-pending');
        this.loadedSlides.add(index);

        // Bind PhotoSwipe click handler to new zoom button
        this.bindPhotoSwipeToSlide(slide);

        console.log('LazyVariantImages: Loaded slide (alt)', index);
      }
    }
  }

  /**
   * Bind PhotoSwipe click handler to a newly loaded slide's zoom button
   * Uses the theme's Photoswipe class for consistent behavior
   */
  bindPhotoSwipeToSlide(slide) {
    const slideIndex = slide.dataset.index;
    console.log('LazyVariantImages: bindPhotoSwipeToSlide called for slide', slideIndex);

    if (!this.settings.zoomEnabled) {
      console.log('LazyVariantImages: Zoom not enabled, skipping bind for slide', slideIndex);
      return;
    }

    const zoomButton = slide.querySelector('.js-photoswipe__zoom');
    if (!zoomButton) {
      console.log('LazyVariantImages: No zoom button found in slide', slideIndex);
      return;
    }

    console.log('LazyVariantImages: Found zoom button in slide', slideIndex);

    // Use the theme's Photoswipe class directly
    // The theme binds using .on() which requires the button to exist at init time
    // So we need to manually trigger the same behavior
    zoomButton.addEventListener('click', (evt) => {
      evt.preventDefault();
      console.log('LazyVariantImages: Zoom button clicked for slide', slideIndex);
      this.triggerPhotoSwipe(slide, evt);
    });
  }

  /**
   * Trigger PhotoSwipe using the theme's Photoswipe class
   * This creates a fresh instance that collects ALL current images
   */
  triggerPhotoSwipe(slide, evt) {
    const slideIndex = slide.dataset.index;
    console.log('LazyVariantImages: triggerPhotoSwipe called for slide', slideIndex);

    // Check if theme.Photoswipe is available
    if (typeof theme === 'undefined' || !theme.Photoswipe) {
      console.log('LazyVariantImages: theme.Photoswipe not found, using fallback');
      this.triggerPhotoSwipeFallback(slide, evt);
      return;
    }

    // Get the image container (same one the theme uses)
    const imageContainer = this.container.querySelector('[data-product-photos]') || this.slidesContainer;
    if (!imageContainer) {
      console.log('LazyVariantImages: Image container not found');
      return;
    }

    // Get section ID for namespace
    const sectionId = this.container.dataset.sectionId || 'product-template';
    console.log('LazyVariantImages: Using section ID:', sectionId);

    // The theme's Photoswipe.triggerClick expects evt.currentTarget to have the zoom button context
    // It uses evt.currentTarget.closest('.product-main-slide').getAttribute('data-index') for grid mode
    // We need to make sure our zoom button is the currentTarget

    // Create a synthetic event object that mimics what the theme expects
    const syntheticEvt = {
      currentTarget: evt.currentTarget,
      preventDefault: () => {},
      stopPropagation: () => {}
    };

    // Create a new Photoswipe instance and manually call triggerClick
    // The constructor calls init() which binds to buttons, but we'll skip that
    // and manually invoke triggerClick with our synthetic event
    try {
      // We can't use the constructor directly because it auto-inits
      // Instead, we manually implement the triggerClick logic
      const container = imageContainer;
      const inSlideshow = container.dataset && container.dataset.hasSlideshow === 'true';

      // Get images based on mode
      const images = inSlideshow
        ? container.querySelectorAll('.flickity-viewport .photoswipe__image')
        : container.querySelectorAll('.photoswipe__image');

      console.log('LazyVariantImages: Found', images.length, 'PhotoSwipe images, inSlideshow:', inSlideshow);

      // Build items array (same as theme's getImageData)
      const items = [];
      images.forEach((el) => {
        // Skip images in hidden slides
        const parentSlide = el.closest('.product-main-slide');
        if (parentSlide && window.getComputedStyle(parentSlide).display === 'none') {
          return;
        }
        items.push({
          msrc: el.currentSrc || el.src,
          src: el.getAttribute('data-photoswipe-src'),
          w: parseInt(el.getAttribute('data-photoswipe-width')) || 1200,
          h: parseInt(el.getAttribute('data-photoswipe-height')) || 1200,
          el: el,
          initialZoomLevel: 0.5
        });
      });

      console.log('LazyVariantImages: Built', items.length, 'items for gallery');

      if (items.length === 0) {
        console.log('LazyVariantImages: No visible images found');
        return;
      }

      // Determine starting index
      let index;
      const mainPhotosEl = document.querySelector('.product__main-photos');
      if (mainPhotosEl && mainPhotosEl.classList.contains('product-grid-layout')) {
        // Grid mode: use data-index from the slide
        const gridItemIndex = parseInt(slide.dataset.index);
        // Find which position this is in the visible items
        const clickedImg = slide.querySelector('.photoswipe__image');
        index = items.findIndex(item => item.el === clickedImg);
        if (index === -1) index = gridItemIndex;
        console.log('LazyVariantImages: Grid mode - calculated index:', index, 'from slide data-index:', gridItemIndex);
      } else {
        // Non-grid mode
        const clickedImg = slide.querySelector('.photoswipe__image');
        index = items.findIndex(item => item.el === clickedImg);
        if (index === -1) index = 0;
        console.log('LazyVariantImages: Non-grid mode - index:', index);
      }

      // Initialize PhotoSwipe gallery (same options as theme)
      const pswpElement = document.querySelector('.pswp');
      if (!pswpElement) {
        console.log('LazyVariantImages: .pswp element not found');
        return;
      }

      const options = {
        allowPanToNext: false,
        captionEl: false,
        closeOnScroll: false,
        counterEl: false,
        history: false,
        index: index,
        pinchToClose: false,
        preloaderEl: false,
        scaleMode: 'zoom',
        shareEl: false,
        tapToToggleControls: false,
        getThumbBoundsFn: function(idx) {
          if (!items[idx] || !items[idx].el) return null;
          var pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
          var thumbnail = items[idx].el;
          var rect = thumbnail.getBoundingClientRect();
          return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
        }
      };

      console.log('LazyVariantImages: Initializing PhotoSwipe at index', index);
      const gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
      gallery.init();
      console.log('LazyVariantImages: PhotoSwipe gallery initialized successfully');

    } catch (err) {
      console.error('LazyVariantImages: Error in triggerPhotoSwipe:', err);
      // Try fallback
      this.triggerPhotoSwipeFallback(slide, evt);
    }
  }

  /**
   * Fallback PhotoSwipe trigger - click on a theme-bound button
   */
  triggerPhotoSwipeFallback(slide, evt) {
    console.log('LazyVariantImages: Using fallback - triggering theme zoom button');

    // Find a zoom button that the theme has already bound handlers to
    // These are buttons in slides that existed when the page loaded
    const themeZoomButtons = this.container.querySelectorAll('.product-main-slide:not(.lazy-pending) .js-photoswipe__zoom');

    if (themeZoomButtons.length > 0) {
      console.log('LazyVariantImages: Found', themeZoomButtons.length, 'theme-bound zoom buttons');
      // Click the first one - the theme's PhotoSwipe will collect all images
      // and the user can navigate to their desired image
      themeZoomButtons[0].click();
    } else {
      console.log('LazyVariantImages: No theme zoom buttons available');
    }
  }

  /**
   * Build image element from placeholder data
   */
  /*
   * SH Updated: August 2026 - Correct Shopify CDN width params on variant gallery images.
   * The previous code did `baseSrc + '?width=N'`, but every data-image-src already carries a
   * query (`...jpg?v=1787217403&width=2048`), so the result was `...&width=2048?width=N`.
   * Shopify's CDN does not parse that second query and serves the full-size original.
   * Measured on a live PDP: as-shipped 381,064 B, malformed `?width=720` 381,064 B (ignored),
   * correct `&width=720` 100,776 B. 15 of 15 gallery images were affected.
   * This helper REPLACES any existing width rather than appending, and picks the right separator.
   */
  shImageUrlWithWidth(src, width) {
    if (!src) return src;
    try {
      const isProtocolRelative = src.slice(0, 2) === '//';
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', width);
      return isProtocolRelative ? url.href.replace(/^https?:/, '') : url.href;
    } catch (e) {
      const sep = src.indexOf('?') === -1 ? '?' : '&';
      return /[?&]width=\d+/.test(src)
        ? src.replace(/([?&])width=\d+/, '$1width=' + width)
        : src + sep + 'width=' + width;
    }
  }

  buildImageElement(slide) {
    const wrapper = document.createElement('div');
    wrapper.className = 'image-wrap loaded';
    wrapper.style.height = '0';
    wrapper.style.paddingBottom = (100 / parseFloat(slide.dataset.aspectRatio)) + '%';

    const imageEl = document.createElement('image-element');
    imageEl.setAttribute('data-aos', slide.dataset.aos || 'image-fade-in');
    imageEl.setAttribute('data-aos-offset', slide.dataset.aosOffset || '150');
    imageEl.classList.add('aos-init');

    const img = document.createElement('img');

    // Basic attributes
    const baseSrc = slide.dataset.imageSrc;
    img.src = this.shImageUrlWithWidth(baseSrc, 800);
    img.alt = slide.dataset.imageAlt || '';
    img.loading = 'lazy';
    img.width = '800';

    // PhotoSwipe attributes
    if (this.settings.zoomEnabled && slide.dataset.photoswipeSrc) {
      img.className = 'photoswipe__image image-element';
      img.setAttribute('data-photoswipe-src', slide.dataset.photoswipeSrc);
      img.setAttribute('data-photoswipe-width', slide.dataset.photoswipeWidth);
      img.setAttribute('data-photoswipe-height', slide.dataset.photoswipeHeight);
    } else {
      img.className = 'image-element';
    }

    img.setAttribute('data-index', slide.dataset.index);

    // Responsive srcset
    img.srcset = [360, 540, 720, 900, 1080]
      .map((w) => `${this.shImageUrlWithWidth(baseSrc, w)} ${w}w`)
      .join(', ');

    // Sizes based on mobile layout
    img.sizes = this.getSizesAttribute();

    imageEl.appendChild(img);
    wrapper.appendChild(imageEl);

    // Add PhotoSwipe zoom button if zoom is enabled
    if (this.settings.zoomEnabled && slide.dataset.photoswipeSrc) {
      const zoomButton = document.createElement('button');
      zoomButton.type = 'button';
      zoomButton.className = 'btn btn--body btn--circle js-photoswipe__zoom product__photo-zoom';
      zoomButton.innerHTML = `
        <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-search" viewBox="0 0 64 64">
          <title>icon-search</title><path d="M47.16 28.58A18.58 18.58 0 1 1 28.58 10a18.58 18.58 0 0 1 18.58 18.58ZM54 54 41.94 42"/>
        </svg>
        <span class="icon__fallback-text">Zoom</span>
      `;
      wrapper.appendChild(zoomButton);
    }

    return wrapper;
  }

  /**
   * Get sizes attribute for responsive images
   */
  getSizesAttribute() {
    if (this.settings.mobileLayout === 'full') {
      return '100vw';
    } else {
      return '(max-width: 768px) 75vw, 100vw';
    }
  }

  /**
   * Schedule background preloading of next variants
   */
  scheduleBackgroundPreload() {
    const preload = () => {
      // Get all variant values in order
      const variantValues = Object.keys(this.imagesByVariant).filter(k => k !== 'untagged');

      // Preload next 1-2 variants in background
      variantValues.slice(0, 2).forEach(variantValue => {
        const images = this.imagesByVariant[variantValue];
        if (images) {
          images.slice(0, 2).forEach(img => {
            this.loadSlide(img.slideIndex);
          });
        }
      });

      console.log('LazyVariantImages: Background preload complete');
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(preload, { timeout: 2000 });
    } else {
      setTimeout(preload, 1500);
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.getElementById('main-product-data')) {
      window.LazyVariantImages = new LazyVariantImages();
    }
  });
} else {
  // DOM already loaded
  if (document.getElementById('main-product-data')) {
    window.LazyVariantImages = new LazyVariantImages();
  }
}
